import React, { useRef, useCallback, memo } from "react";
import { useDispatch } from "react-redux";

import { formatTimestamp, getUniqueTagsWithSystemFlag, renderModuleType }   from "../../containers/Diaries/util";
import FileView               from "../FileView";
import UserImage              from '../UserImage/index';
import Icon                   from "../../icons";
import DiaryDetailPopUp       from "./index";
import { updateCaptionTodo, resetViewDiaryInfo } from "../../containers/Diaries/actions";
import { getUserInfo }        from "../../containers/SpockViewPage/actions/action";
import { showDialogueBox, hideDialogueBox } from "../DialogueBox/actions";

// Sub-Components
const StatusDateRow = memo(({ status, currentStatus, label, date, testId }) => {
    if (currentStatus !== status || !date) return null;
    return (
        <div className="reminder mb mt" data-test={testId}>
            <label>{label}</label>
            <date>{date}</date>
        </div>
    );
});

const OwnerInfo = ({ ownerView, onMouseEnter, onMouseLeave }) => {
  let fullName  = `${ownerView.first_name} ${ownerView.last_name}`.trim();
  return (
    <ul className="rowSpaceBetween alignMiddle flexWrap">
        <li data-test="DiaryOwner">
            <span className="userGroup diaryUser">
                <div className="rowFlex">
                    <div className="userPic userPicIcon" onMouseEnter={onMouseEnter} onMouseLeave={onMouseLeave}>
                        <UserImage
                            profileUrl={ownerView.image.profile}
                            profileName={fullName}
                        />
                    </div>
                    <div className="userInfo flexWrap">
                        <span>{fullName}</span>
                        <label>Diary Owner</label>
                    </div>
                </div>
            </span>
        </li>
        <li>
            <div className="rowFlex wrapFlex alignMiddle diaryOwner">
                <span className="quickTooltip-top alignMiddle mr__1" data-test="OwnerMobile">
                    <Icon name="mobile" className="mobile svg-call" />
                    {ownerView.phone || 'NA'}
                </span>
                <span className="quickTooltip-top alignMiddle email rightAlign" data-test="OwnerEmail">
                    <Icon name="email" className="email svg-email" />
                    {ownerView.email}
                </span>
            </div>
        </li>
    </ul>
)};

const PriorityFlag = memo(({ priority, isCompleted, status }) => {
    if (priority !== 3 || isCompleted || status !== "OPEN") return null;
    return (
        <span className="priority animated superFaster flagHigh delay-5 mr">
            <Icon name="flag" className="icon ml" />
        </span>
    );
});

const Detail = ({ data, dataKey, userTimeZone, galleryData, status }) => {
    const dispatch  = useDispatch();
    const timerRef  = useRef(null);
    const ownerView = data.owner ?? {};

    // Handlers
    const openMiniProfile = useCallback((targetRect) => {
        dispatch(getUserInfo({ target: targetRect, userId: data.OwnerId }));
    }, [dispatch, data.OwnerId]);

    const onMouseEnter = useCallback((e) => {
        const rect = e.currentTarget.getBoundingClientRect();
        timerRef.current = setTimeout(() => openMiniProfile(rect), 500);
    }, [openMiniProfile]);

    const onMouseLeave = useCallback(() => {
        clearTimeout(timerRef.current);
    }, []);

    const deleteCaptionCallBack = useCallback(() => {
        dispatch(showDialogueBox({
            flag:      true,
            component: (
                <DiaryDetailPopUp
                    dataKey={dataKey}
                    onClose={() => {
                        dispatch(hideDialogueBox());
                        dispatch(resetViewDiaryInfo());
                    }}
                />
            ),
            overlayClassName:        "modal fullViewMain",
            contentClassName:        "modalContent animated faster fadeInUpx2 medium fullView",
            shouldCloseOnOverlayClick: false,
        }));
    }, [dispatch, dataKey]);

    const updateCaption = useCallback((payload) => {
        dispatch(updateCaptionTodo(payload));
    }, [dispatch]);

    // Render
    const hasFiles = data.fileData?.Files?.length > 0;

    // Tags/Systemtags handler
    const uniqueTags = getUniqueTagsWithSystemFlag(data?.tags, data?.system_tags);

    return (
        <>
          <div className="wkDetailInfo">
              <div className="diaryWkTitle alignMiddle">
                  {renderModuleType(data, true)}
              </div>
              <OwnerInfo
                  ownerView={ownerView}
                  onMouseEnter={onMouseEnter}
                  onMouseLeave={onMouseLeave}
              />
          </div>

          <div className="wkDetailInfo">
              <ul className="rowFlex wrapFlex">
                  <li data-test="Priority">
                      <label>Priority</label>
                      <span>
                          {data.priority_title}
                          <PriorityFlag
                              priority={data?.priority_title}
                              isCompleted={data?.completed}
                              status={data?.status}
                          />
                      </span>
                  </li>
                  <li data-test="Due On">
                      <label>Due On</label>
                      <span>
                          {data?.due_date
                              ? formatTimestamp(data?.due_date, data?.due_date_tz, "datetime")
                              : "-"}
                      </span>
                  </li>
                  <li data-test="Reminder On">
                      <label>Reminder On</label>
                      <span>
                          {data?.PreetyReminderDate
                              ? formatTimestamp(data?.due_date, data?.ReminderTz, "datetime")
                              : "-"}
                      </span>
                  </li>
              </ul>
          </div>

          <StatusDateRow
              status="COMPLETED"
              currentStatus={data.status}
              label="Completed On"
              date={data?.PreetyCompletedDate} //todo
              testId="Completed On"
          />
          <StatusDateRow
              status="CANCELLED"
              currentStatus={data.status}
              label="Cancelled On"
              date={data?.PreetyDeletedDate}
              testId="Cancelled On"
          />

          {/* ── Tags ── */}
          <div className="mb mt formTags">
              <label>Tags</label>
              {
                  uniqueTags.map(({ tag, isSystemTag }) => (
                      <label key={tag} className={`mr status tags ${isSystemTag ? "ReactTags__tag" : ""}`}>{tag}</label>
                  ))
              }
          </div>

          {/* ── Attachment ── */}
          <div className="attachMent mb" data-test="Attachment">
              <label>Attachment</label>
              {hasFiles ? (
                  <div className="fileAttachment addFile">
                      <FileView
                          fileData={data.fileData}
                          galleryData={galleryData}
                          extraData={{ isDiaryDetailPopUp: true, deleteCaptionCallBack }}
                          updateFileCaption={updateCaption}
                      />
                  </div>
              ) : (
                  <p>No Files available.</p>
              )}
          </div>

          <div className="description">
              <label>Description</label>
              {data.Description
                  ? <p className="descriptionFormating" dangerouslySetInnerHTML={{ __html: data.Description }} />
                  : "-"
              }
          </div>
        </>
    );
};

export default Detail;